const React = require('react');
const Joi = require('joi');
const { default: Box } = require('@mui/material/Box');
const {
    OFFER_CREATION_METHODS,
    STUDENT_OFFER_ROLE_IDS,
    USER_ROLE_GROUPS_IDS,
    USER_ROLE_IDS
} = require('utils/constants');
const OfferImageField = require('./OfferImageField');

const FORM_ID = 'admin-offer-form';
const UPLOAD_FORM_ID = 'admin-offer-upload-form';
const DEFAULT_CTA_BG_COLOR = '#000000';
const DEFAULT_CTA_TEXT_COLOR = '#ffffff';
const ALL_ROLES_VALUE = '__ALL__';

module.exports.FORM_ID = FORM_ID;
module.exports.UPLOAD_FORM_ID = UPLOAD_FORM_ID;
module.exports.ALL_ROLES_VALUE = ALL_ROLES_VALUE;

module.exports.isImageUploadOffer = function isImageUploadOffer(offer) {

    return offer?.creationMethod === OFFER_CREATION_METHODS.IMAGE_UPLOAD;
};

module.exports.getOfferEditPath = function getOfferEditPath(offer) {

    if (!offer?.id) {
        return '/app/admin/offers';
    }

    if (module.exports.isImageUploadOffer(offer)) {
        return `/app/admin/offers/${offer.id}/edit/upload`;
    }

    return `/app/admin/offers/${offer.id}/edit`;
};

module.exports.isProspectOfferRole = function isProspectOfferRole(role = {}) {

    if (USER_ROLE_IDS.PROSPECT.includes(Number(role.id))) {
        return true;
    }

    if (role.roleGroupId != null) {
        return String(role.roleGroupId) === String(USER_ROLE_GROUPS_IDS.PROSPECT);
    }

    return false;
};

module.exports.getStudentOfferRoles = function getStudentOfferRoles(roles = [], persistedRoleIds = []) {

    const persistedIds = new Set(persistedRoleIds.map(String));
    const matched = roles
        .filter((role) => {

            if (persistedIds.has(String(role.id))
                || STUDENT_OFFER_ROLE_IDS.includes(Number(role.id))
                || USER_ROLE_IDS.PROSPECT.includes(Number(role.id))) {
                return true;
            }

            if (role.roleGroupId != null) {
                return String(role.roleGroupId) === String(USER_ROLE_GROUPS_IDS.STUDENT)
                    || String(role.roleGroupId) === String(USER_ROLE_GROUPS_IDS.PROSPECT);
            }

            return USER_ROLE_IDS.STUDENT.includes(Number(role.id));
        })
        .map((role) => ({
            id: role.id,
            label: role.label || role.name,
            roleGroupId: role.roleGroupId
        }));
    const seen = new Set(matched.map((role) => String(role.id)));

    persistedRoleIds.forEach((id) => {

        if (seen.has(String(id))) {
            return;
        }

        const fromRoles = roles.find((role) => String(role.id) === String(id));
        matched.push({
            id: Number(id),
            label: fromRoles?.label || fromRoles?.name || `Role ${id}`,
            roleGroupId: fromRoles?.roleGroupId
        });
        seen.add(String(id));
    });

    return matched;
};

module.exports.resolveStudentRoleSelection = function resolveStudentRoleSelection({
    allRoleIds = [],
    previousAllStudentsSelected = false,
    value = []
} = {}) {

    const roleIds = value.filter((roleId) => roleId !== ALL_ROLES_VALUE);
    const allStudentsSelected = value.includes(ALL_ROLES_VALUE);

    if (allStudentsSelected && !previousAllStudentsSelected) {
        return {
            roleIds: allRoleIds,
            allStudentsSelected: true
        };
    }

    if (allStudentsSelected) {
        return {
            roleIds,
            allStudentsSelected: false
        };
    }

    return {
        roleIds: previousAllStudentsSelected ? [] : roleIds,
        allStudentsSelected: false
    };
};

module.exports.EMPTY_DEFAULTS = {
    brandName: '',
    brandLogoUrl: '',
    tagText: '',
    tagColor: '#000000',
    tagTextColor: '#ffffff',
    tagFont: '',
    title: '',
    subtitle: '',
    mediaType: '',
    heroImageUrl: '',
    heroVideoUrl: '',
    couponCode: '',
    terms: '',
    expirationDate: null,
    ctaText: '',
    ctaUrl: '',
    ctaDeepLink: '',
    ctaBgColor: '',
    ctaTextColor: '',
    startDate: null,
    endDate: null,
    status: 'draft',
    priority: 100,
    order: '',
    partnerSuccessUrl: '',
    redemptionCallbackUrl: '',
    redemptionNotes: '',
    sendPushNotifications: false
};

module.exports.UPLOAD_EMPTY_DEFAULTS = {
    brandName: '',
    brandLogoUrl: '',
    tagText: '',
    tagColor: '#000000',
    tagTextColor: '',
    tagFont: '',
    title: '',
    ctaText: 'Get Offer',
    ctaUrl: '',
    ctaDeepLink: '',
    ctaBgColor: '',
    ctaTextColor: '',
    status: 'draft',
    priority: 100,
    startDate: null,
    endDate: null,
    order: '',
    partnerSuccessUrl: '',
    redemptionCallbackUrl: '',
    redemptionNotes: '',
    sendPushNotifications: false
};

module.exports.withCtaColorDefaults = function withCtaColorDefaults(defaults = {}, schoolBrandColor) {

    return {
        ...defaults,
        ctaBgColor: defaults.ctaBgColor || DEFAULT_CTA_BG_COLOR,
        ctaTextColor: defaults.ctaTextColor || DEFAULT_CTA_TEXT_COLOR
    };
};

module.exports.shouldConfirmPushNotifications = function shouldConfirmPushNotifications(defaults = {}, formData = {}) {

    return !defaults.sendPushNotifications && !!formData.sendPushNotifications;
};

module.exports.buildFormConfig = function buildFormConfig(props = {}) {

    const { defaults, onSubmit } = props;

    return {
        formId: FORM_ID,
        fields: internals.fields,
        defaults: defaults || module.exports.EMPTY_DEFAULTS,
        schema: internals.schema,
        onSubmit
    };
};

module.exports.buildUploadFormConfig = function buildUploadFormConfig(props = {}) {

    const { defaults, onSubmit } = props;

    return {
        formId: UPLOAD_FORM_ID,
        fields: internals.uploadFields,
        defaults: defaults || module.exports.UPLOAD_EMPTY_DEFAULTS,
        schema: internals.uploadSchema,
        onSubmit
    };
};

module.exports.buildTargetingRowFormConfig = function buildTargetingRowFormConfig(props = {}) {

    const {
        allRolesLabel = 'All Students',
        allStudentsSelected = false,
        availableRoles = [],
        emptyRolesLabel = 'Select roles',
        isLoading = false,
        onRolesChange,
        onSchoolChange,
        roleIds = [],
        rolesFieldLabel = 'Roles',
        rowKey,
        schoolId,
        schools = [],
        schoolsLoading = false
    } = props;
    const studentRoleIds = allStudentsSelected
        ? [ALL_ROLES_VALUE, ...roleIds]
        : roleIds;

    return {
        formId: `offer-targeting-row-${rowKey}`,
        defaults: {
            schoolId: schoolId || '',
            studentRoleIds
        },
        fields: [{
            type: 'group',
            columns: 2,
            gap: 1,
            fields: [
                {
                    name: 'schoolId',
                    type: 'SelectField',
                    label: 'School',
                    options: schools.map((school) => ({
                        value: school.id,
                        label: school.name
                    })),
                    onChange: onSchoolChange,
                    textFieldProps: {
                        id: `target-school-${rowKey}`,
                        variant: 'filled',
                        disabled: schoolsLoading,
                        InputLabelProps: {
                            shrink: true
                        }
                    }
                },
                {
                    name: 'studentRoleIds',
                    type: 'MultiSelectField',
                    label: rolesFieldLabel,
                    options: [
                        { value: ALL_ROLES_VALUE, label: allRolesLabel },
                        ...availableRoles.map((role) => ({
                            value: role.id,
                            label: role.label
                        }))
                    ],
                    onChange: onRolesChange,
                    renderValue: () => isLoading
                        ? 'Loading…'
                        : internals.renderTargetRoleValue(
                            availableRoles,
                            roleIds,
                            allStudentsSelected,
                            {
                                allSelectedLabel: allRolesLabel,
                                emptyRolesLabel
                            }
                        ),
                    formControlProps: {
                        disabled: !schoolId || isLoading || availableRoles.length === 0,
                        variant: 'filled'
                    },
                    inputLabelProps: {
                        shrink: true
                    },
                    selectProps: {
                        displayEmpty: true,
                        labelId: `target-student-roles-${rowKey}-label`,
                        inputProps: {
                            id: `target-student-roles-${rowKey}`
                        }
                    }
                }
            ]
        }],
        onSubmit: internals.noop
    };
};

module.exports.buildImageOfferPayload = function buildImageOfferPayload(imageUrl, formData) {

    return {
        brandName: formData.brandName,
        brandLogoUrl: formData.brandLogoUrl,
        tagText: formData.tagText || null,
        tagColor: formData.tagColor || null,
        tagTextColor: formData.tagTextColor || null,
        ctaBgColor: formData.ctaBgColor || null,
        ctaTextColor: formData.ctaTextColor || null,
        tagFont: formData.tagFont || null,
        title: formData.title,
        mediaType: 'image',
        heroImageUrl: imageUrl,
        ctaText: formData.ctaText,
        ctaUrl: formData.ctaUrl,
        ctaDeepLink: formData.ctaDeepLink || null,
        status: formData.status,
        priority: internals.clampPriority(formData.priority),
        startDate: formData.startDate,
        endDate: formData.endDate,
        order: formData.order !== '' && formData.order != null ? Number(formData.order) : null,
        partnerSuccessUrl: formData.partnerSuccessUrl || null,
        redemptionCallbackUrl: formData.redemptionCallbackUrl || null,
        redemptionNotes: formData.redemptionNotes || null,
        sendPushNotifications: !!formData.sendPushNotifications
    };
};

module.exports.buildOfferPayload = function buildOfferPayload(formData = {}) {

    return Object.entries(formData).reduce((payload, [key, value]) => {

        if (typeof value === 'undefined') {
            return payload;
        }

        if (typeof value?.toISOString === 'function') {
            payload[key] = value.toISOString();
            return payload;
        }

        if (internals.NUMBER_FIELDS.has(key)) {
            payload[key] = internals.normalizeNumberField(key, value);
            return payload;
        }

        payload[key] = value === '' ? null : value;
        return payload;
    }, {});
};

const internals = {};

internals.noop = () => undefined;

internals.renderTargetRoleValue = (availableRoles, roleIds, allStudentsSelected, copy = {}) => {

    if (roleIds.length === 0) {
        return <Box component='span' sx={{ color: 'text.secondary' }}>{copy.emptyRolesLabel || 'Select roles'}</Box>;
    }

    if (allStudentsSelected) {
        return copy.allSelectedLabel || 'All Students';
    }

    return availableRoles
        .filter((role) => roleIds.includes(role.id))
        .map((role) => role.label)
        .join(', ');
};

internals.NUMBER_FIELDS = new Set(['order', 'priority']);

internals.PRIORITY_MIN = 1;
internals.PRIORITY_MAX = 100;
internals.PRIORITY_DEFAULT = 100;

internals.clampPriority = (value) => {

    if (value === '' || value === null || typeof value === 'undefined') {
        return internals.PRIORITY_DEFAULT;
    }

    const numberValue = Math.trunc(Number(value));

    if (Number.isNaN(numberValue)) {
        return internals.PRIORITY_DEFAULT;
    }

    return Math.min(internals.PRIORITY_MAX, Math.max(internals.PRIORITY_MIN, numberValue));
};

// Use a text input (not type="number") so characters like "-" and "." are captured
// and surfaced through schema validation instead of being silently swallowed by the browser.
internals.priorityFieldProps = {
    inputProps: {
        inputMode: 'numeric'
    }
};

internals.prioritySchema = Joi.number()
    .integer()
    .min(internals.PRIORITY_MIN)
    .max(internals.PRIORITY_MAX)
    .allow('', null)
    .messages({
        'number.base': 'Priority must be a whole number — only digits 0-9 are allowed',
        'number.integer': 'Priority must be a whole number — decimals are not allowed',
        'number.unsafe': 'Priority must be a whole number — only digits 0-9 are allowed',
        'number.min': `Priority must be between ${internals.PRIORITY_MIN} and ${internals.PRIORITY_MAX}`,
        'number.max': `Priority must be between ${internals.PRIORITY_MIN} and ${internals.PRIORITY_MAX}`
    });

internals.normalizeNumberField = (field, value) => {

    if (field === 'priority') {
        return internals.clampPriority(value);
    }

    if (value === '' || value === null) {
        return null;
    }

    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? null : numberValue;
};

internals.STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'disabled', label: 'Disabled' }
];

internals.MEDIA_TYPE_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' }
];

internals.brandSectionFields = [
    { type: 'title', text: 'Brand' },
    { name: 'brandName', label: 'Brand Name', type: 'TextField', required: true, fullWidth: true },
    {
        name: 'brandLogoUrl',
        label: 'Brand Logo',
        type: 'TextField',
        required: true,
        fullWidth: true,
        component: OfferImageField,
        componentProps: {
            required: true
        }
    },
    { name: 'tagText', label: 'Tag Text (e.g. "New", "Exclusive")', type: 'TextField', fullWidth: true },
    { name: 'tagColor', label: 'Tag Color (hex, e.g. #e53935)', type: 'ColorField', fullWidth: false },
    { name: 'tagTextColor', label: 'Tag Text Color (RGB)', type: 'ColorField', fullWidth: false },
    { name: 'tagFont', label: 'Tag Font (optional)', type: 'TextField', fullWidth: true }
];

internals.uploadFields = [
    ...internals.brandSectionFields,
    { type: 'divider' },
    { type: 'title', text: 'Content' },
    { name: 'title', label: 'Title', type: 'TextField', required: true, fullWidth: true },
    { type: 'divider' },
    { type: 'title', text: 'CTA' },
    { name: 'ctaText', label: 'CTA Button Label (e.g. "Get Offer")', type: 'TextField', required: true, fullWidth: true },
    { name: 'ctaBgColor', label: 'CTA Background Color', type: 'ColorField', fullWidth: false },
    { name: 'ctaTextColor', label: 'CTA Text Color', type: 'ColorField', fullWidth: false },
    { name: 'ctaUrl', label: 'CTA URL', type: 'TextField', required: true, fullWidth: true },
    { name: 'ctaDeepLink', label: 'CTA Deep Link (optional)', type: 'TextField', fullWidth: true },
    { type: 'divider' },
    { type: 'title', text: 'Scheduling & Status' },
    { name: 'status', label: 'Status', type: 'SelectField', fullWidth: true, options: internals.STATUS_OPTIONS },
    { name: 'sendPushNotifications', label: 'Send this offer as a push notification', type: 'SwitchField', offLabel: 'In-app only', onLabel: 'Send push', fullWidth: true },
    { name: 'priority', label: 'Priority (1-100, lower = higher priority)', type: 'TextField', fullWidth: true, textFieldProps: internals.priorityFieldProps },
    { name: 'startDate', label: 'Start Date', type: 'DateTimeField', fullWidth: true },
    { name: 'endDate', label: 'End Date', type: 'DateTimeField', fullWidth: true },
    { name: 'order', label: 'Carousel Order (optional)', type: 'TextField', fullWidth: true, textFieldProps: { type: 'number' } },
    { type: 'divider' },
    { type: 'title', text: 'Redemption' },
    { name: 'partnerSuccessUrl', label: 'Partner Success URL', type: 'TextField', fullWidth: true },
    { name: 'redemptionCallbackUrl', label: 'Redemption Callback URL', type: 'TextField', fullWidth: true },
    { name: 'redemptionNotes', label: 'Redemption Notes (internal)', type: 'TextareaField', fullWidth: true, textFieldProps: { minRows: 2 } }
];

internals.uploadSchema = Joi.object({
    brandName: Joi.string().required().messages({ 'string.empty': 'Brand Name is required' }),
    brandLogoUrl: Joi.string().required().messages({ 'string.empty': 'Brand Logo is required' }),
    title: Joi.string().required().messages({ 'string.empty': 'Title is required' }),
    tagText: Joi.string().allow('', null),
    tagColor: Joi.string().allow('', null),
    tagTextColor: Joi.string().allow('', null),
    tagFont: Joi.string().allow('', null),
    ctaText: Joi.string().required().messages({ 'string.empty': 'CTA Button Label is required' }),
    ctaUrl: Joi.string().required().messages({ 'string.empty': 'CTA URL is required' }),
    ctaBgColor: Joi.string().allow('', null),
    ctaTextColor: Joi.string().allow('', null),
    ctaDeepLink: Joi.string().allow('', null),
    ctaBgColor: Joi.string().allow('', null),
    ctaTextColor: Joi.string().allow('', null),
    status: Joi.string().valid('draft', 'pending', 'approved', 'rejected', 'disabled'),
    sendPushNotifications: Joi.boolean(),
    priority: internals.prioritySchema,
    startDate: Joi.any().allow(null),
    endDate: Joi.any().allow(null),
    order: Joi.any().allow('', null),
    partnerSuccessUrl: Joi.string().allow('', null),
    redemptionCallbackUrl: Joi.string().allow('', null),
    redemptionNotes: Joi.string().allow('', null)
});

internals.fields = [
    ...internals.brandSectionFields,
    { type: 'divider' },
    { type: 'title', text: 'Content' },
    { name: 'title', label: 'Title', type: 'TextField', required: true, fullWidth: true },
    { name: 'subtitle', label: 'Body Text', type: 'TextareaField', fullWidth: true, textFieldProps: { minRows: 2 } },
    { name: 'mediaType', label: 'Media Type', type: 'SelectField', fullWidth: true, options: internals.MEDIA_TYPE_OPTIONS },
    { name: 'heroImageUrl', label: 'Hero Image URL', type: 'TextField', fullWidth: true },
    { name: 'heroVideoUrl', label: 'Hero Video URL', type: 'TextField', fullWidth: true },
    { name: 'couponCode', label: 'Coupon Code', type: 'TextField', fullWidth: true },
    { name: 'terms', label: 'Terms & Conditions', type: 'TextareaField', fullWidth: true, textFieldProps: { minRows: 3 } },
    { name: 'expirationDate', label: 'Expiration Date', type: 'DateTimeField', fullWidth: true },
    { type: 'divider' },
    { type: 'title', text: 'CTA' },
    { name: 'ctaText', label: 'CTA Button Label (e.g. "Get Offer")', type: 'TextField', required: true, fullWidth: true },
    { name: 'ctaBgColor', label: 'CTA Background Color', type: 'ColorField', fullWidth: false },
    { name: 'ctaTextColor', label: 'CTA Text Color', type: 'ColorField', fullWidth: false },
    { name: 'ctaUrl', label: 'CTA URL', type: 'TextField', required: true, fullWidth: true },
    { name: 'ctaDeepLink', label: 'CTA Deep Link (optional)', type: 'TextField', fullWidth: true },
    { type: 'divider' },
    { type: 'title', text: 'Scheduling & Status' },
    { name: 'status', label: 'Status', type: 'SelectField', fullWidth: true, options: internals.STATUS_OPTIONS },
    { name: 'sendPushNotifications', label: 'Send this offer as a push notification', type: 'SwitchField', offLabel: 'In-app only', onLabel: 'Send push', fullWidth: true },
    { name: 'priority', label: 'Priority (1-100, lower = higher priority)', type: 'TextField', fullWidth: true, textFieldProps: internals.priorityFieldProps },
    { name: 'startDate', label: 'Start Date', type: 'DateTimeField', fullWidth: true },
    { name: 'endDate', label: 'End Date', type: 'DateTimeField', fullWidth: true },
    { name: 'order', label: 'Carousel Order (optional)', type: 'TextField', fullWidth: true, textFieldProps: { type: 'number' } },
    { type: 'divider' },
    { type: 'title', text: 'Redemption' },
    { name: 'partnerSuccessUrl', label: 'Partner Success URL', type: 'TextField', fullWidth: true },
    { name: 'redemptionCallbackUrl', label: 'Redemption Callback URL', type: 'TextField', fullWidth: true },
    { name: 'redemptionNotes', label: 'Redemption Notes (internal)', type: 'TextareaField', fullWidth: true, textFieldProps: { minRows: 2 } }
];

internals.schema = Joi.object({
    brandName: Joi.string().required().messages({ 'string.empty': 'Brand Name is required' }),
    brandLogoUrl: Joi.string().required().messages({ 'string.empty': 'Brand Logo is required' }),
    title: Joi.string().required().messages({ 'string.empty': 'Title is required' }),
    ctaText: Joi.string().required().messages({ 'string.empty': 'CTA Button Label is required' }),
    ctaUrl: Joi.string().required().messages({ 'string.empty': 'CTA URL is required' }),
    tagText: Joi.string().allow('', null),
    tagColor: Joi.string().allow('', null),
    tagTextColor: Joi.string().allow('', null),
    tagFont: Joi.string().allow('', null),
    subtitle: Joi.string().allow('', null),
    mediaType: Joi.string().valid('image', 'video').allow('', null),
    heroImageUrl: Joi.string().allow('', null),
    heroVideoUrl: Joi.string().allow('', null),
    couponCode: Joi.string().allow('', null),
    terms: Joi.string().allow('', null),
    expirationDate: Joi.any().allow(null),
    ctaDeepLink: Joi.string().allow('', null),
    startDate: Joi.any().allow(null),
    endDate: Joi.any().allow(null),
    status: Joi.string().valid('draft', 'pending', 'approved', 'rejected', 'disabled'),
    sendPushNotifications: Joi.boolean(),
    priority: internals.prioritySchema,
    order: Joi.any().allow('', null),
    partnerSuccessUrl: Joi.string().allow('', null),
    redemptionCallbackUrl: Joi.string().allow('', null),
    redemptionNotes: Joi.string().allow('', null)
}).options({ allowUnknown: true });

module.exports.internals = internals;
